/**
 * KAN-858: "Что сделать" вверху карточки клиента приложения менеджера:
 * причина строки внимания (как во вкладке "Внимание"), задача менеджеру со
 * сроком (просрочена: красным) и пометка следующего контакта. Под ними
 * основное действие: "Взять в работу" / "Назначить" (те же кнопки и права,
 * что в блоке "Итог") и "Ответить" (к полю ввода, если писать можно).
 * Сказать нечего: ничего.
 */
import { MessageSquare } from 'lucide-react';
import { formatAttentionDate } from '@/components/dashboard/attentionFormat';
import { MobileWorkButtons } from '@/components/mobile/MobileWorkButtons';
import { useClientManagerRights } from '@/hooks/useClientManagerRights';
import { useClientAttentionItem } from '@/hooks/useMobileData';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import type { MobileWork } from '@/hooks/useMobileWork';
import { focusMobileComposer, managerTaskView, REPLY_LABEL } from '@/lib/mobileChat';
import { cn } from '@/lib/utils';
import type { Client } from '@/types/client';

interface Props {
  client: Client;
  work: Pick<MobileWork, 'canTake' | 'canAssign' | 'assignLabel' | 'take' | 'assign'>;
  busy?: boolean;
}

export function MobileManagerTask({ client, work, busy = false }: Props): JSX.Element | null {
  const { data: item } = useClientAttentionItem(client.id);
  const { canChange } = useClientManagerRights(client.manager?.id);
  const timeZone = useOrgTimezone();
  const view = managerTaskView(item, client);
  if (!view) return null;
  return (
    <section
      className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 p-4"
      data-testid="mobile-manager-task"
    >
      <h2 className="text-base font-semibold text-amber-950">Что сделать</h2>
      {view.reason && (
        <p className="text-base text-gray-900" data-testid="mobile-manager-task-reason">
          {view.reason}
        </p>
      )}
      {view.task && (
        <p
          className={cn('text-sm', view.task.overdue ? 'text-red-700' : 'text-gray-800')}
          data-testid="mobile-manager-task-task"
        >
          {view.task.text}
          <span className="whitespace-nowrap">
            {' '}
            до {formatAttentionDate(view.task.due_at, timeZone)}
            {view.task.overdue && ', просрочено'}
          </span>
        </p>
      )}
      {view.note && (
        <p className="text-sm text-gray-800" data-testid="mobile-manager-task-note">
          {view.note}
        </p>
      )}
      <MobileWorkButtons work={work} disabled={busy} testIdPrefix="mobile-task-work" />
      {canChange && (
        <button
          type="button"
          onClick={focusMobileComposer}
          className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-base font-medium text-gray-900"
          data-testid="mobile-manager-task-reply"
        >
          <MessageSquare className="h-5 w-5" aria-hidden="true" />
          {REPLY_LABEL}
        </button>
      )}
    </section>
  );
}
